import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Phone,
  Mail,
  MapPin,
  ArrowUpRight,
  ChevronRight,
  Linkedin,
  Github,
  Instagram,
  Facebook,
  Link2,
  Layers,
  Building2,
  MapPinned,
} from "lucide-react";
import { landingPages } from "../data/landingPages";

const socialLinks = [
  { name: "LinkedIn", icon: Linkedin, url: "https://www.linkedin.com/company/maxiom-technology" },
  { name: "GitHub", icon: Github, url: "https://github.com/maxiomtech" },
  { name: "Instagram", icon: Instagram, url: "https://www.instagram.com/maxiomtech" },
  { name: "Facebook", icon: Facebook, url: "https://www.facebook.com/maxiomtech" },
  { name: "Clutch", icon: Link2, url: "https://clutch.co/profile/maxiom-technology" },
];

/** Shown in footer preview; full directories live in the home browse hub. */
const SPECIALTY_PREVIEW_MAX = 6;
const INDUSTRY_PREVIEW_MAX = 10;

const partnerSites = [
  { name: "Maxiom Technology", url: "https://www.maxiomtech.com" },
  { name: "Maxiom Developers", url: "https://maxiom.dev" },
  { name: "Maxiom Labs", url: "https://www.maxiomlabs.com" },
  { name: "CTO 2.0", url: "https://www.cto20.com" },
  { name: "Hyperlogs", url: "https://www.hyperlogs.com" },
];

function landingsByCategory(category) {
  return landingPages
    .filter((p) => p.pageType === "seo" && p.category === category)
    .sort((a, b) =>
      String(a.shortTitle || a.title).localeCompare(String(b.shortTitle || b.title), undefined, {
        sensitivity: "base",
      }),
    );
}

const services = [
  {
    name: "Mobile App Development",
    path: "/services/mobile-app-development",
    children: [
      { name: "iOS App Development", path: "/services/mobile-app-development/ios" },
      { name: "Android App Development", path: "/services/mobile-app-development/android" },
      { name: "Hybrid & Cross-Platform", path: "/services/mobile-app-development/hybrid-cross-platform" },
      { name: "Low-Code & RMAD", path: "/services/mobile-app-development/low-code-rmad" },
    ],
  },
  { name: "Web App Development", path: "/services/web-app-development" },
  { name: "Custom Software Development", path: "/services/custom-software" },
  { name: "E-commerce Development", path: "/services/ecommerce" },
  { name: "AI & Machine Learning", path: "/services/ai-ml" },
  { name: "Security Audits", path: "/services/security-audits" },
];

const company = [
  { name: "Case Studies", path: "/case-studies" },
  { name: "Our Process", path: "/process" },
  { name: "Blog", path: "/blog" },
  { name: "Free App Idea Assessment", path: "/lp/free-app-idea-assessment" },
  { name: "Free App Cost Estimate", path: "/lp/free-app-cost-estimate" },
  { name: "Contact Us", path: "/contact" },
];

export default function Footer() {
  const specialtyServices = useMemo(() => landingsByCategory("service"), []);
  const industrySolutions = useMemo(() => landingsByCategory("industry"), []);
  const locationPages = useMemo(() => landingsByCategory("location"), []);

  const specialtyPreview = specialtyServices.slice(0, SPECIALTY_PREVIEW_MAX);
  const industryPreview = industrySolutions.slice(0, INDUSTRY_PREVIEW_MAX);

  const exploreLinkClass =
    "text-gray-400 hover:text-[#00AEEF] text-sm transition-colors leading-snug [text-wrap:pretty]";
  const exploreBrowseClass =
    "group inline-flex items-center gap-1 text-[#00AEEF]/90 hover:text-[#00AEEF] text-xs font-semibold tracking-wide mt-5";

  const exploreColumns = [
    {
      key: "specialty",
      title: "Specialty pages",
      icon: Layers,
      iconClass: "bg-[#8B5CF6]/15 text-[#A78BFA] border-[#8B5CF6]/25",
      pages: specialtyPreview,
      total: specialtyServices.length,
      browseTo: { pathname: "/", search: "?browseHub=services", hash: "#browse-solutions" },
      browseLabel: "All specialty pages",
      testPrefix: "footer-specialty",
      listCols: "grid-cols-1",
    },
    {
      key: "industry",
      total: industrySolutions.length,
      title: "Industries",
      icon: Building2,
      iconClass: "bg-[#10B981]/15 text-[#34D399] border-[#10B981]/25",
      pages: industryPreview,
      browseTo: { pathname: "/", search: "?browseHub=industries", hash: "#browse-solutions" },
      browseLabel: "All industries",
      testPrefix: "footer-industry",
      listCols: "sm:grid-cols-2",
    },
    {
      key: "location",
      title: "Locations",
      icon: MapPinned,
      iconClass: "bg-[#00AEEF]/12 text-[#00AEEF] border-[#00AEEF]/25",
      pages: locationPages,
      total: locationPages.length,
      browseTo: { pathname: "/", search: "?browseHub=regions", hash: "#browse-solutions" },
      browseLabel: "Regions & metros",
      testPrefix: "footer-location",
      listCols: "sm:grid-cols-2",
    },
  ];

  return (
    <footer data-testid="site-footer" className="bg-[#0A0A0A] border-t border-white/5">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl py-16 md:py-20">
        {/* Primary: core navigation only */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="md:col-span-2 lg:col-span-1">
            <Link to="/" data-testid="footer-logo" className="inline-block mb-6">
              <img
                src="/maxiom-apps-logo.png"
                alt="Maxiom Apps"
                className="w-[240px] h-auto"
              />
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              Custom mobile, web, and AI software from an Ashburn, VA studio. Startup MVPs through enterprise platforms.
            </p>
            {/* Social Media Icons */}
            <div className="flex items-center gap-3 mb-6">
              {socialLinks.map((social) => (
                <a
                  key={social.name}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 hover:text-[#00AEEF] hover:border-[#00AEEF]/50 transition-all duration-300"
                  data-testid={`footer-social-${social.name.toLowerCase()}`}
                  aria-label={social.name}
                >
                  <social.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
            {/* Partner Sites */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {partnerSites.map((site) => (
                <a
                  key={site.name}
                  href={site.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-[#00AEEF] text-sm transition-colors flex items-center gap-1"
                  data-testid={`footer-partner-${site.name.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '')}`}
                >
                  {site.name} <ArrowUpRight className="w-3 h-3" />
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-medium text-sm uppercase tracking-widest mb-6">Services</h4>
            <ul className="space-y-3">
              {services.map((s) => (
                <li key={s.path}>
                  <Link
                    to={s.path}
                    className="text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                    data-testid={`footer-service-${s.path.split("/").pop()}`}
                  >
                    {s.name}
                  </Link>
                  {s.children && (
                    <ul className="mt-1 space-y-1 pl-4 border-l border-white/10">
                      {s.children.map((child) => (
                        <li key={child.path}>
                          <Link
                            to={child.path}
                            className="text-gray-500 hover:text-[#00AEEF] text-xs transition-colors"
                            data-testid={`footer-service-${child.path.split("/").pop()}`}
                          >
                            {child.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-medium text-sm uppercase tracking-widest mb-6">Company</h4>
            <ul className="space-y-3">
              {/* External links moved to top of list */}
              <li>
                <a
                  href="https://www.maxiomtech.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                >
                  Maxiom Technology
                </a>
              </li>
              <li>
                <a
                  href="https://maxiom.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                >
                  Maxiom Developers
                </a>
              </li>
              <li>
                <a
                  href="https://www.maxiomlabs.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                >
                  Maxiom Labs
                </a>
              </li>
              <li>
                <a
                  href="https://www.cto20.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                >
                  Hire CTOs
                </a>
              </li>

              {company.map((c) => (
                <li key={c.path}>
                  <Link
                    to={c.path}
                    className="text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                    data-testid={`footer-company-${c.path.split("/").pop()}`}
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-2 lg:col-span-1">
            <h4 className="text-white font-medium text-sm uppercase tracking-widest mb-6">Get in Touch</h4>
            <ul className="space-y-4">
              <li>
                <a
                  href="tel:7039429420"
                  className="flex items-center gap-3 text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 text-[#00AEEF]" />
                  703-942-9420
                </a>
              </li>
              <li>
                <a
                  href="mailto:hello@maxiomtech.com"
                  className="flex items-center gap-3 text-gray-400 hover:text-[#00AEEF] text-sm transition-colors"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 text-[#00AEEF]" />
                  hello@maxiomtech.com
                </a>
              </li>
              <li className="flex items-start gap-3 text-gray-400 text-sm">
                <MapPin className="w-4 h-4 text-[#00AEEF] mt-0.5 flex-shrink-0" />
                <span>
                  Maxiom Technology<br />
                  44927 George Washington Blvd<br />
                  Suite 265<br />
                  Ashburn, VA 20147
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Topic deep-links: one module: header + balanced columns */}
        <div
          id="footer-explore-by-topic"
          className="mt-14 pt-10 border-t border-white/5 scroll-mt-24"
          data-testid="footer-explore-section"
        >
          <div className="rounded-2xl border border-white/8 bg-[#0d0d0d] shadow-[0_0_0_1px_rgba(255,255,255,0.04)_inset] overflow-hidden">
            <div className="px-5 py-5 sm:px-6 sm:py-6 md:px-8 border-b border-white/6 bg-white/[0.02]">
              <h3 className="text-white font-medium text-sm uppercase tracking-[0.2em]">
                Explore by topic
              </h3>
              <p className="text-gray-500 text-xs sm:text-sm mt-2 max-w-2xl leading-relaxed">
                Jump to common topics here, or open the browse hub on the home page for filters and the full list.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 lg:divide-x lg:divide-white/6 lg:items-stretch">
              {exploreColumns.map((col) => {
                const Icon = col.icon;
                const shown = col.pages.length;
                const total = col.total;
                const rangeLabel =
                  shown >= total ? `${total} pages` : `${shown} of ${total} pages`;
                return (
                  <div
                    key={col.key}
                    className="flex flex-col min-h-0 min-w-0 px-5 py-6 sm:px-6 md:px-8 md:py-7 border-b border-white/6 last:border-b-0 lg:border-b-0"
                  >
                    <div className="flex items-start gap-3 mb-5">
                      <span
                        className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${col.iconClass}`}
                        aria-hidden
                      >
                        <Icon className="h-4 w-4" strokeWidth={2} />
                      </span>
                      <div className="min-w-0 pt-0.5">
                        <h4 className="text-white font-medium text-xs uppercase tracking-wider">
                          {col.title}
                        </h4>
                        <p className="text-gray-600 text-[11px] mt-1 leading-snug">{rangeLabel}</p>
                      </div>
                    </div>

                    <ul className={`grid gap-x-5 gap-y-2.5 flex-1 content-start auto-rows-min ${col.listCols}`}>
                      {col.pages.map((p) => (
                        <li key={p.path} className="min-w-0">
                          <Link
                            to={p.path}
                            className={exploreLinkClass}
                            data-testid={`${col.testPrefix}-${p.path.replace(/^\//, "")}`}
                          >
                            {p.shortTitle || p.title}
                          </Link>
                        </li>
                      ))}
                    </ul>

                    <Link to={col.browseTo} className={`${exploreBrowseClass} mt-auto shrink-0`}>
                      {col.browseLabel}
                      <ChevronRight
                        className="h-3.5 w-3.5 opacity-80 transition-transform group-hover:translate-x-0.5"
                        aria-hidden
                      />
                    </Link>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/5 mt-16 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-xs" data-testid="footer-copyright">
            &copy; {new Date().getFullYear()} Maxiom Apps. All rights reserved.
          </p>
          <div className="flex flex-col items-center gap-3 md:flex-row md:gap-6">
            <a
              href="https://clutch.co/profile/maxiom-technology"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xs text-gray-200 hover:border-[#00AEEF]/60 hover:bg-[#00AEEF]/10 transition-colors"
              data-testid="footer-clutch-badge"
            >
              <span className="flex h-5 w-16 items-center justify-center rounded bg-transparent text-[10px] font-bold text-[#F59E0B] tracking-tight leading-none">
                ★★★★★
              </span>
              <span className="font-semibold text-white">Top-rated on Clutch</span>
              <span className="text-gray-400 hidden sm:inline">
                Champion &amp; Global Leader
              </span>
            </a>
            <div className="flex items-center gap-6">
              <a href="https://www.maxiomtech.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">
                Privacy Policy
              </a>
              <a href="https://www.maxiomtech.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 text-xs transition-colors">
                Terms of Service
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
